import { formatSignedUsd, formatUsd, toNumber } from "#/lib/formatters.ts";
import type {
	AccountInfoMetric,
	UseAccountInfoParams,
} from "../types/account-info.types.ts";

export function useAccountInfo({
	accountState,
	positions,
	walletBalance,
	isUnified = false,
	isDexAbstraction = false,
	spotUsdc = 0,
	spotUsdcHold = 0,
}: UseAccountInfoParams) {
	const perpBalance = toNumber(
		accountState.marginSummary?.accountValue ??
			accountState.crossMarginSummary.accountValue,
	);
	const crossPerpBalance = toNumber(
		accountState.crossMarginSummary.accountValue,
	);
	const unrealizedPnl = positions.reduce(
		(total, position) => total + toNumber(position.unrealizedPnl),
		0,
	);
	const balance = perpBalance - unrealizedPnl;
	const maintenanceMargin = toNumber(accountState.crossMaintenanceMarginUsed);
	const notionalPosition = Math.abs(
		toNumber(accountState.crossMarginSummary.totalNtlPos),
	);

	// Unified account formula (from HL docs):
	//   ratio = crossMaintenanceMarginUsed / (spotTotal - isolatedMarginUsed)
	// Non-unified:
	//   ratio = crossMaintenanceMarginUsed / crossAccountValue
	const isolatedMarginUsed = positions.reduce(
		(total, pos) =>
			total +
			(pos.leverage?.type === "isolated"
				? Math.abs(toNumber(pos.leverage.rawUsd))
				: 0),
		0,
	);
	// See ACCOUNT.md → Balance display rules.
	// Equity = positions MTM + free spot. spotUsdcHold is already inside
	// perpBalance for unified, so `spot - hold` extracts the free portion.
	const tradingBalance =
		isUnified || isDexAbstraction
			? perpBalance + spotUsdc - spotUsdcHold
			: perpBalance;

	const marginDenominator = isUnified
		? perpBalance - isolatedMarginUsed
		: crossPerpBalance;
	const crossMarginRatio =
		marginDenominator > 0 ? (maintenanceMargin / marginDenominator) * 100 : 0;
	const crossAccountLeverage =
		marginDenominator > 0 ? notionalPosition / marginDenominator : 0;

	const balanceMetrics: AccountInfoMetric[] = [
		{
			label: "account.wallet-usdc",
			value: `${walletBalance} USDC`,
			tooltip: "account.wallet-usdc-tooltip",
		},
		{
			label: "account.trading-account",
			value: formatUsd(tradingBalance),
			tooltip: "account.trading-account-tooltip",
		},
	];

	const overviewMetrics: AccountInfoMetric[] = [
		{ label: "account.balance", value: formatUsd(balance) },
		{
			label: "account.unrealized-pnl",
			value: formatSignedUsd(unrealizedPnl),
			valueClassName:
				unrealizedPnl >= 0
					? "text-[var(--color-theme-status-positive)]"
					: "text-[var(--color-theme-status-negative)]",
		},
		{
			label: "account.cross-margin-ratio",
			value: `${crossMarginRatio.toFixed(2)}%`,
		},
		{
			label: "account.maintenance-margin",
			value: formatUsd(maintenanceMargin),
		},
		{
			label: "account.cross-leverage",
			value: `${crossAccountLeverage.toFixed(2)}x`,
		},
	];

	return {
		balanceMetrics,
		overviewMetrics,
		tradingBalance,
		walletBalance,
	};
}
